import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { WorkspaceKeyPermission } from '~/types/WorkspaceKeyTypes';

export enum UIActionType {
  TOGGLE_DRAWER = 'TOGGLE_DRAWER',
  SET_SELECTED_PROCESS_ID = 'SET_SELECTED_PROCESS_ID',
  SET_SELECTED_ARTIFACT_RUN_ID = 'SET_SELECTED_ARTIFACT_RUN_ID',
  SET_SELECTED_ACTIVITY_RUN_ID = 'SET_SELECTED_ACTIVITY_RUN_ID',
  SET_CURRENT_WORKSPACE_ID = 'SET_CURRENT_WORKSPACE_ID',
  TOGGLE_CONSOLE_TIMESTAMP = 'TOGGLE_CONSOLE_TIMESTAMP',
  TOGGLE_SLACK_DIALOG = 'TOGGLE_SLACK_DIALOG',
  TOGGLE_WS_KEY_DIALOG = 'TOGGLE_WS_KEY_DIALOG',
  TOGGLE_FEEDBACK_DIALOG = 'TOGGLE_FEEDBACK_DIALOG',
  TOGGLE_SERVICE_DESK_DIALOG = 'TOGGLE_SERVICE_DESK_DIALOG',
  TOGGLE_ENVIRONMENT_GROUP_LINK_DIALOG = 'TOGGLE_ENVIRONMENT_GROUP_LINK_DIALOG',
  SET_CURRENT_ORGANIZATION_ID = 'SET_CURRENT_ORGANIZATION_ID',
  CREATE_WORKSPACE_FOR_ORG = 'CREATE_WORKSPACE_FOR_ORG',
  SET_SELECTED_ORGANIZATION_ID = 'SET_SELECTED_ORGANIZATION_ID',
  TOGGLE_ORGANIZATION_DIALOG = 'TOGGLE_ORGANIZATION_DIALOG',
  SET_SELECTED_PLAN_ID = 'SET_SELECTED_PLAN_ID',
  SET_ROBOT_FILTER = 'SET_ROBOT_FILTER',
  SET_SELECTED_RUNTIME_ID = 'SET_SELECTED_RUNTIME_ID',
  SET_PROCESS_METRICS_TIMESPAN = 'SET_PROCESS_METRICS_TIMESPAN',
  SET_SELECTED_STATISTICS_PROCESS_ID = 'SET_SELECTED_STATISTICS_PROCESS_ID',
  SET_PROCESSES_STATISTICS_FILTER = 'SET_PROCESSES_STATISTICS_FILTER',
  TOGGLE_NEW_NAVIGATION = 'TOGGLE_NEW_NAVIGATION',
  TOGGLE_NEW_AUDIT_LOGS = 'TOGGLE_NEW_AUDIT_LOGS',
  SET_ASSISTANT_RUNS_CURSOR = 'SET_ASSISTANT_RUNS_CURSOR',
  TOGGLE_ONBOARDING_COMPLETED_DIALOG = 'TOGGLE_ONBOARDING_COMPLETED_DIALOG',
  SET_CREATING_ONBOARDING_PROCESS = 'SET_CREATING_ONBOARDING_PROCESS',
}

type WithId = { id: string };
type WithOrganizationId = { organizationId: string };
type WithKeyId = { keyId?: string };
type WithStringValue = { value: string };

type WithFlags = {
  flags?: WorkspaceKeyPermission[];
};

type WithDatePeriod = {
  value: {
    startDate: Date;
    endDate: Date;
  };
};

type ToggleDrawer = Action<UIActionType.TOGGLE_DRAWER>;
type ToggleSlackDialog = Action<UIActionType.TOGGLE_SLACK_DIALOG>;
type ToggleWsKeyDialog = Action<UIActionType.TOGGLE_WS_KEY_DIALOG> & WithKeyId & WithFlags;
type ToggleFeedbackDialog = Action<UIActionType.TOGGLE_FEEDBACK_DIALOG>;
type ToggleServiceDeskDialog = Action<UIActionType.TOGGLE_SERVICE_DESK_DIALOG>;
type SetSelectedProcessId = Action<UIActionType.SET_SELECTED_PROCESS_ID> & WithId;
type SetSelectedActivityRunId = Action<UIActionType.SET_SELECTED_ACTIVITY_RUN_ID> & WithId;
type SetArtifactRunId = Action<UIActionType.SET_SELECTED_ARTIFACT_RUN_ID> & WithId;
type SetCurrentWorkspaceId = Action<UIActionType.SET_CURRENT_WORKSPACE_ID> & WithId;
type SetCurrentOrganizationId = Action<UIActionType.SET_CURRENT_ORGANIZATION_ID> & WithId;
type ToggleConsoleTimestamp = Action<UIActionType.TOGGLE_CONSOLE_TIMESTAMP>;
type CreateWorkspaceForOrg = Action<UIActionType.CREATE_WORKSPACE_FOR_ORG> & WithOrganizationId;
type ToggleOrganizationDialog = Action<UIActionType.TOGGLE_ORGANIZATION_DIALOG>;
type SetSelectedPlanId = Action<UIActionType.SET_SELECTED_PLAN_ID> & WithId;
type SetSelectedRuntimeId = Action<UIActionType.SET_SELECTED_RUNTIME_ID> & WithId;
type SetProcessMetricsTimespan = Action<UIActionType.SET_PROCESS_METRICS_TIMESPAN> & WithDatePeriod;
type SetSelectedStatisticsProcessId = Action<UIActionType.SET_SELECTED_STATISTICS_PROCESS_ID> &
  WithId;
type SetProcessesStatisticsFilter = Action<UIActionType.SET_PROCESSES_STATISTICS_FILTER> &
  WithStringValue;
type ToggleNewNavigation = Action<UIActionType.TOGGLE_NEW_NAVIGATION>;
type SetAssistantRunsCursor = Action<UIActionType.SET_ASSISTANT_RUNS_CURSOR> & WithStringValue;
type ToggleOnboardingCompletedDialog = Action<UIActionType.TOGGLE_ONBOARDING_COMPLETED_DIALOG>;
type SetCreatingOnboardingProcess = Action<UIActionType.SET_CREATING_ONBOARDING_PROCESS>;

export type UIAction =
  | ToggleDrawer
  | ToggleSlackDialog
  | ToggleFeedbackDialog
  | ToggleWsKeyDialog
  | ToggleServiceDeskDialog
  | SetSelectedProcessId
  | SetArtifactRunId
  | SetSelectedActivityRunId
  | SetCurrentWorkspaceId
  | SetCurrentOrganizationId
  | ToggleConsoleTimestamp
  | CreateWorkspaceForOrg
  | ToggleOrganizationDialog
  | SetSelectedPlanId
  | SetSelectedRuntimeId
  | SetProcessMetricsTimespan
  | SetSelectedStatisticsProcessId
  | SetProcessesStatisticsFilter
  | ToggleNewNavigation
  | SetAssistantRunsCursor
  | ToggleOnboardingCompletedDialog
  | SetCreatingOnboardingProcess;

export const toggleDrawer = (): ThunkAction<void, null, null, ToggleDrawer> => (dispatch) => {
  dispatch({
    type: UIActionType.TOGGLE_DRAWER,
  });
};

export const createWorkspaceForOrg =
  (organizationId: string): ThunkAction<void, null, null, CreateWorkspaceForOrg> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.CREATE_WORKSPACE_FOR_ORG,
      organizationId,
    });
  };

export const toggleOrganizationDialog =
  (): ThunkAction<void, null, null, ToggleOrganizationDialog> => (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_ORGANIZATION_DIALOG,
    });
  };

export const toggleSlackDialog =
  (): ThunkAction<void, null, null, ToggleSlackDialog> => (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_SLACK_DIALOG,
    });
  };

export const toggleWsKeyDialog =
  (
    keyId?: string,
    preFilledFlags?: WorkspaceKeyPermission[],
  ): ThunkAction<void, null, null, ToggleWsKeyDialog> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_WS_KEY_DIALOG,
      keyId,
      flags: preFilledFlags,
    });
  };

export const toggleFeedbackDialog =
  (): ThunkAction<void, null, null, ToggleFeedbackDialog> => (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_FEEDBACK_DIALOG,
    });
  };

export const toggleServiceDeskDialog =
  (): ThunkAction<void, null, null, ToggleServiceDeskDialog> => (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_SERVICE_DESK_DIALOG,
    });
  };

export const setSelectedProcessId =
  (id: string): ThunkAction<void, null, null, SetSelectedProcessId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_SELECTED_PROCESS_ID,
      id,
    });
  };

export const setSelectedActivityRunId =
  (id: string): ThunkAction<void, null, null, SetSelectedActivityRunId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_SELECTED_ACTIVITY_RUN_ID,
      id,
    });
  };

/**
 * This is used to control which run id artifacts are sorted.
 * Both robot and assistant runs have artifacts.
 * @param id
 */
export const setSelectedArtifactRunId =
  (id: string): ThunkAction<void, null, null, SetArtifactRunId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_SELECTED_ARTIFACT_RUN_ID,
      id,
    });
  };

export const setCurrentWorkspaceId =
  (id: string): ThunkAction<void, null, null, SetCurrentWorkspaceId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_CURRENT_WORKSPACE_ID,
      id,
    });
  };

export const setCurrentOrganizationId =
  (id: string): ThunkAction<void, null, null, SetCurrentOrganizationId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_CURRENT_ORGANIZATION_ID,
      id,
    });
  };

export const setSelectedPlanId =
  (id: string): ThunkAction<void, null, null, SetSelectedPlanId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_SELECTED_PLAN_ID,
      id,
    });
  };

export const toggleConsoleTimestamp =
  (): ThunkAction<void, null, null, ToggleConsoleTimestamp> => (dispatch) => {
    dispatch({ type: UIActionType.TOGGLE_CONSOLE_TIMESTAMP });
  };

export const setSelectedRuntimeId =
  (id: string): ThunkAction<void, null, null, SetSelectedRuntimeId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_SELECTED_RUNTIME_ID,
      id,
    });
  };

export const setSelectedProcessMetricsTimespan =
  (startDate: Date, endDate: Date): ThunkAction<void, null, null, SetProcessMetricsTimespan> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_PROCESS_METRICS_TIMESPAN,
      value: {
        startDate,
        endDate,
      },
    });
  };

export const setSelectedStatisticsProcessId =
  (id: string): ThunkAction<void, null, null, SetSelectedStatisticsProcessId> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_SELECTED_STATISTICS_PROCESS_ID,
      id,
    });
  };

export const setProcessesStatisticsFilter =
  (value: string): ThunkAction<void, null, null, SetProcessesStatisticsFilter> =>
  (dispatch) => {
    dispatch({
      type: UIActionType.SET_PROCESSES_STATISTICS_FILTER,
      value,
    });
  };

export const toggleNewNavigation =
  (): ThunkAction<void, null, null, ToggleNewNavigation> => (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_NEW_NAVIGATION,
    });
  };

export const toggleOnboardingCompletedDialog =
  (): ThunkAction<void, null, null, ToggleOnboardingCompletedDialog> => (dispatch) => {
    dispatch({
      type: UIActionType.TOGGLE_ONBOARDING_COMPLETED_DIALOG,
    });
  };

export const setCreatingOnboardingProcess =
  (): ThunkAction<void, null, null, SetCreatingOnboardingProcess> => (dispatch) => {
    dispatch({
      type: UIActionType.SET_CREATING_ONBOARDING_PROCESS,
    });
  };
